import type React from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faPhone, faUserShield } from '@fortawesome/free-solid-svg-icons';

type Testimonial = {
  name: string;
  image: string;
  location: string;
  text: React.JSX.Element;
};

/**
 * Home page component displayed at `/` and `/home`.
 */
export const Landing = () => {
  const testimonials: Array<Testimonial> = [
    {
      name: 'Manuel Guzman',
      location: 'San Antonio, TX',
      image: '/assets/home/testimonial-manuel.png',
      text: (
        <>
          <p className="small">
            After months of looking for a CRM, there was not another like MedicarePro. As an
            independent agent it has helped me get organized with my client's policies and contact
            information. Other CRM's are just not niched to the Medicare business and they are so
            complicated. The support is also above and beyond. I love MedicarePRO CRM!
          </p>
        </>
      ),
    },
    {
      name: 'Gina Geiger',
      location: 'Kettering, OH',
      image: '/assets/home/testimonial-gina.png',
      text: (
        <>
          <p className="small">
            "I have been using MedicarePRO CRM for roughly two years now and love it. I use the CRM
            to track my clients, prospects and pended/issued business. I love the functions it
            offers to add alerts for each client as well as that it will alert me by text message if
            I want it to. The functions available with this CRM are user friendly and by far way
            better than any other CRM I have used in the past!"
          </p>
        </>
      ),
    },
    {
      name: 'Samuel Patton',
      location: 'Minden, LA',
      image: '/assets/home/testimonial-samuel.png',
      text: (
        <>
          <p className="small">
            "I've signed up for many CRM trials over the years and cancelled them before I paid any
            money. Medicare Pro was different. Once I started using it, I was like, “Yes, please
            take my money!” I couldn't go back. I just loved using MedicarePro! There are so many
            thoughtful features designed into it. I could go on and on about all the unique and
            thoughtful touches inside the MedicarePro CRM but you owe it to yourself to check it out
            by signing up for your own free trial."
          </p>
        </>
      ),
    },
    {
      name: 'Amber Clay',
      location: 'Seminole, OK',
      image: '/assets/home/testimonial-amber.png',
      text: (
        <>
          <p className="small">
            "MedicarePro CRM is a perfect program for my agency. I love the fact that it is simple
            to use and can be used for my personal business and for keeping track of my agencies
            business. MedicarePro Support is absolutely on top of everything! Any issues I have had
            have been resolved very quickly. MedicarePro has even made adjustments to fine tuning
            exactly what I need a CRM to do for my agency. I have worked with other CRM's and by far
            this company has been the best. I highly recommend MedicarePro CRM!!"
          </p>
        </>
      ),
    },
    {
      name: 'Ike English',
      location: 'Columbia, SC',
      image: '/assets/home/testimonial-ike.png',
      text: (
        <>
          <p className="small">
            "When I was introduced to MedicarePRO CRM, I was blown away. The CRM I was using for my
            Life & Health clients doesn't compare. MedicarePRO CRM has allowed me to gain more
            control over my day, save time, and simplify the data entry process. The dashboard
            layout has a clean appearance and is very informative. I enjoy being able to track all
            of my commissions and renewals in one place and watch them grow! MedicarePRO CRM gives
            me peace of mind about the information I'm saving in the CRM because it's HIPAA
            compliant. Last but not least, the customer service is AMAZING!!"
          </p>
        </>
      ),
    },
    {
      name: 'Fred Lewis',
      location: 'Star, ID',
      image: '/assets/home/testimonial-fred.png',
      text: (
        <>
          <p className="small">
            "I highly recommend MedicarePRO/CRM for any agency or agent in the Medicare Insurance
            business. It is an easy program to use and fast to learn so you can hit the ground
            running. We started with MedicarePRO/CRM right before the beginning of (AEP) Annual
            Enrollment Period for 2020. Using this CRM help us become more efficient and better
            serve our clients. The program has many features that are useful. One of my favorites is
            the source tracking of clients and prospects. This allows me to see where every single
            lead comes from and I can better focus my marketing dollars going forward. With all the
            program's useful features, the best thing about MedicarePRO/CRM is the support. They
            were fast to respond to any and all questions or issues we came across. The system has
            been great, and I can see how useful it will be as our agency grows. If you are in the
            market for a good CRM, try the MedicarePRO/CRM trial run"
          </p>
        </>
      ),
    },
    {
      name: 'John Salois',
      location: 'Providence, RI',
      image: '/assets/home/testimonial-john.png',
      text: (
        <>
          <p className="small">
            "I used a CRM provided by my MGA for years and was very happy with it. The MGA decided
            to change CRM venders and that change disrupted my process and some data that I had been
            utilizing for marketing was lost. I searched for a new solution and was ready to start
            from scratch with my own CRM when I found Medicare Pro. Medicare Pro offered the
            features and simplicity I was looking for and their support was always available to help
            with utilization. They even made updates based on my needs and I am always pleasantly
            surprised when new features are added. Medicare Pro is an essential part of my
            business's success"
          </p>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="intro">
        <Container className="pt-5 pb-5 max-w-xl">
          <Row className="pt-5 mt-5">
            <Col lg={4} className="pt-5">
              <h1 className="text-white fw-bold">Industry standard CRM and sales tool.</h1>
              <p className="text-white lead">
                Take your insurance business with you wherever, whenever.
              </p>
              <NavLink className="btn btn-primary" to="/home/sign-up" end>
                Start Free Trial
              </NavLink>
            </Col>
            <Col lg={{ span: 7, offset: 1 }}>
              <img src="/assets/home/computer-preview.png" className="w-100" />
            </Col>
          </Row>
        </Container>
      </div>
      <Container className="pt-5 pb-5 max-w-xl">
        <Row>
          <Col>
            <h1 className="text-center fw-bold mt-5">Designed for agents & agencies</h1>
            <div className="header-divider" />
            <p className="text-center mt-4 mb-5 px-5">
              Medicare can be complex, your back office shouldn't be. Manage your clients and agents
              with ease.
            </p>
          </Col>
        </Row>
        <Row>
          <Col xs={6} className="text-center">
            <img src="/assets/home/landing-foragents.png" className="w-100 rounded" />
          </Col>
          <Col xs={6} className="text-center">
            <img src="/assets/home/landing-foragencies.png" className="w-100 rounded" />
          </Col>
          <Col xs={6} className="text-center">
            <NavLink className="btn btn-primary mt-2" to="/home/agents" end>
              For Agents
            </NavLink>
          </Col>
          <Col xs={6} className="text-center">
            <NavLink className="btn btn-primary mt-2" to="/home/agencies" end>
              For Agencies
            </NavLink>
          </Col>
        </Row>
      </Container>
      <div className="bg-white">
        <Container className="pt-5 max-w-xl">
          <h1 className="text-center fw-bold mt-5">The features your business needs</h1>
          <div className="header-divider" />
          <p className="text-center mt-4 mb-5 px-5">
            We strive to provide the absolute best user experience across every platform.
          </p>

          <Row>
            <Col xs={4}>
              <FontAwesomeIcon className="mt-5 text-primary h2 w-auto" icon={faFileContract} />
              <h5 className="fw-bold">Predictable pricing</h5>
              <p>
                Leave complex pricing structures behind. Always know what you pay per month and
                never worry about hidden fees or rate hikes.
              </p>
            </Col>
            <Col xs={4}>
              <FontAwesomeIcon className="mt-5 text-primary h2 w-auto" icon={faPhone} />
              <h5 className="fw-bold">World-class service</h5>
              <p>
                Whatever you need we are always here to help. We try to answer your call on the
                first try and respond to emails super fast. You'll feel the love.
              </p>
            </Col>
            <Col xs={4}>
              <FontAwesomeIcon className="mt-5 text-primary h2 w-auto" icon={faUserShield} />
              <h5 className="fw-bold">Security</h5>
              <p>
                Security is our number one priority. High level data encryption and daily backups
                ensure sensitive information stays safe and HIPAA compliant.
              </p>
            </Col>
            <Col xs={12} className="text-center py-5">
              <img src="/assets/home/hippa.png" />
            </Col>
          </Row>
        </Container>
      </div>
      <Container className="max-w-xl">
        <h1 className="text-center fw-bold mt-5">The features your business needs</h1>
        <div className="header-divider mb-5" />
        <Row>
          {testimonials.map((testimonial) => (
            <Col key={testimonial.name} className="mb-3" xs={4}>
              <Card className="testimonial border bg-white">
                <Card.Body className="text-center px-4">
                  <img
                    src={testimonial.image}
                    className="testimonial-image testimonial-image-m mt-2"
                  />
                  <h6 className="fw-bold mt-3 mb-0">{testimonial.name}</h6>
                  <h6 className="fw-bold text-secondary mt-1 mb-4">{testimonial.location}</h6>
                  {testimonial.text}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
      <Container className="pt-5 pb-5 text-center">
        <h1 className="text-center fw-bold mt-5">Give it a try</h1>
        <div className="header-divider" />
        <p className="text-center mt-4 mb-5 px-5 lead">
          We give you 30 days to try out MedicarePRO with unlimited access. No card info, no
          contracts.
        </p>
        <NavLink className="btn btn-primary" to="/home/sign-up" end>
          Start Free Trial
        </NavLink>
      </Container>
    </>
  );
};
